import { useState, useRef, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import {
  Send,
  Plus,
  Loader2,
  PanelLeftClose,
  PanelLeft,
  ChevronDown,
  Settings2,
  User,
  Bot,
  Trash2,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

interface ChatResponse {
  content: string;
  model: string;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

type ModelId = 'gpt-4.1' | 'gpt-4.1-mini' | 'gpt-4.1-nano';

const MODELS: { id: ModelId; label: string; description: string }[] = [
  { id: 'gpt-4.1', label: 'GPT-4.1', description: 'Most capable' },
  { id: 'gpt-4.1-mini', label: 'GPT-4.1 Mini', description: 'Balanced' },
  { id: 'gpt-4.1-nano', label: 'GPT-4.1 Nano', description: 'Fastest' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let messageCounter = 0;
function nextId(): string {
  messageCounter += 1;
  return `msg_${Date.now()}_${messageCounter}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Playground() {
  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);

  // Chat parameters
  const [model, setModel] = useState<ModelId>('gpt-4.1');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(4096);
  const [systemPrompt, setSystemPrompt] = useState('');

  // Conversation
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modelDropdownRef = useRef<HTMLDivElement>(null);

  // ---------------------------------------------------------------------------
  // Auto-scroll
  // ---------------------------------------------------------------------------

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  // ---------------------------------------------------------------------------
  // Auto-resize textarea
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const lineHeight = 24;
    const maxHeight = lineHeight * 6;
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
  }, [inputValue]);

  // ---------------------------------------------------------------------------
  // Close model dropdown on outside click
  // ---------------------------------------------------------------------------

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target as Node)) {
        setModelDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ---------------------------------------------------------------------------
  // Send message
  // ---------------------------------------------------------------------------

  const sendMessage = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isLoading) return;

    const userMessage: ChatMessage = {
      id: nextId(),
      role: 'user',
      content: trimmed,
      timestamp: Date.now(),
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInputValue('');
    setIsLoading(true);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      const apiMessages = updatedMessages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const response = await api<ChatResponse>('ai/chat', {
        method: 'POST',
        body: {
          model,
          messages: apiMessages,
          temperature,
          max_tokens: maxTokens,
          ...(systemPrompt.trim() ? { system_prompt: systemPrompt.trim() } : {}),
        },
      });

      const assistantMessage: ChatMessage = {
        id: nextId(),
        role: 'assistant',
        content: response.content,
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      const errorContent =
        err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.';

      const errorMessage: ChatMessage = {
        id: nextId(),
        role: 'assistant',
        content: `[Error] ${errorContent}`,
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      // Refocus the textarea after the response
      textareaRef.current?.focus();
    }
  }, [inputValue, isLoading, messages, model, temperature, maxTokens, systemPrompt]);

  // ---------------------------------------------------------------------------
  // Keyboard handling
  // ---------------------------------------------------------------------------

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ---------------------------------------------------------------------------
  // New chat
  // ---------------------------------------------------------------------------

  const handleNewChat = () => {
    setMessages([]);
    setInputValue('');
    setIsLoading(false);
    textareaRef.current?.focus();
  };

  // ---------------------------------------------------------------------------
  // Selected model info
  // ---------------------------------------------------------------------------

  // Model is always one of the defined IDs, so the find will always match.
  // Fall back to a literal object to satisfy the type checker.
  const selectedModel = MODELS.find((m) => m.id === model) ?? {
    id: model,
    label: model,
    description: '',
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex h-full overflow-hidden">
      {/* ------------------------------------------------------------------ */}
      {/* Left Sidebar                                                        */}
      {/* ------------------------------------------------------------------ */}
      {sidebarOpen && (
        <aside className="w-80 flex-shrink-0 border-r border-border bg-card flex flex-col overflow-hidden">
          {/* Sidebar header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Settings2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-semibold text-foreground">Configuration</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 rounded hover:bg-accent text-muted-foreground"
              title="Collapse sidebar"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </div>

          {/* Sidebar body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-5">
            {/* Model selector */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Model</label>
              <div className="relative" ref={modelDropdownRef}>
                <button
                  type="button"
                  onClick={() => setModelDropdownOpen((v) => !v)}
                  className="w-full flex items-center justify-between border border-border rounded-md px-3 py-2 bg-background text-sm text-foreground hover:border-primary/50 transition-colors"
                >
                  <span>{selectedModel.label}</span>
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform ${modelDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {modelDropdownOpen && (
                  <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-card shadow-lg">
                    {MODELS.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => {
                          setModel(m.id);
                          setModelDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors first:rounded-t-md last:rounded-b-md ${
                          m.id === model ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'
                        }`}
                      >
                        <div>{m.label}</div>
                        <div className="text-xs text-muted-foreground">{m.description}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Temperature slider */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-foreground">Temperature</label>
                <span className="text-xs font-mono text-muted-foreground tabular-nums">
                  {temperature.toFixed(1)}
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={2}
                step={0.1}
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
                className="w-full accent-primary h-1.5 rounded-lg cursor-pointer"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>Precise</span>
                <span>Creative</span>
              </div>
            </div>

            {/* Max tokens */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Max Tokens</label>
              <input
                type="number"
                min={1}
                max={128000}
                value={maxTokens}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val > 0) setMaxTokens(val);
                }}
                className="w-full border border-border rounded-md px-3 py-2 bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              />
            </div>

            {/* System prompt */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                System Prompt
              </label>
              <textarea
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="You are a helpful assistant..."
                rows={4}
                className="w-full border border-border rounded-md px-3 py-2 bg-background text-sm text-foreground resize-y focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary placeholder:text-muted-foreground"
              />
            </div>
          </div>

          {/* Sidebar footer */}
          <div className="p-4 border-t border-border space-y-2">
            <button
              onClick={handleNewChat}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Chat
            </button>
            {messages.length > 0 && (
              <button
                onClick={handleNewChat}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-border text-muted-foreground hover:bg-accent hover:text-foreground text-sm transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Clear Messages
              </button>
            )}
          </div>
        </aside>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Main Chat Area                                                      */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Chat header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-card">
          {!sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-1 rounded hover:bg-accent text-muted-foreground"
              title="Expand sidebar"
            >
              <PanelLeft className="h-4 w-4" />
            </button>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-semibold text-foreground">AI Playground</h1>
            <p className="text-xs text-muted-foreground">
              {selectedModel.label} | Temp {temperature.toFixed(1)} | Max {maxTokens.toLocaleString()} tokens
            </p>
          </div>
          {messages.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {messages.length} message{messages.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-6">
          {messages.length === 0 && !isLoading ? (
            <EmptyState onSelectPrompt={(prompt) => {
              setInputValue(prompt);
              textareaRef.current?.focus();
            }} />
          ) : (
            <div className="max-w-3xl mx-auto space-y-4">
              {messages.map((msg) => (
                <MessageBubble key={msg.id} message={msg} />
              ))}
              {isLoading && <TypingIndicator />}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="border-t border-border bg-card px-4 py-3">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-end gap-2">
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message... (Shift+Enter for new line)"
                  rows={1}
                  disabled={isLoading}
                  className="w-full border border-border rounded-md px-3 py-2.5 bg-background text-sm text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary placeholder:text-muted-foreground disabled:opacity-50"
                  style={{ minHeight: '42px' }}
                />
              </div>
              <button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="flex-shrink-0 h-[42px] w-[42px] flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Send message"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 text-center">
              Enter to send, Shift+Enter for new line
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';
  const isError = !isUser && message.content.startsWith('[Error]');

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar */}
      <div
        className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${
          isUser ? 'bg-primary/10' : 'bg-muted'
        }`}
      >
        {isUser ? (
          <User className="h-4 w-4 text-primary" />
        ) : (
          <Bot className="h-4 w-4 text-muted-foreground" />
        )}
      </div>

      {/* Content */}
      <div
        className={`max-w-[75%] rounded-lg px-4 py-2.5 text-sm ${
          isUser
            ? 'bg-primary text-primary-foreground'
            : isError
              ? 'bg-destructive/10 text-destructive border border-destructive/20'
              : 'bg-muted text-foreground'
        }`}
      >
        <p className="whitespace-pre-wrap break-words leading-relaxed">{message.content}</p>
        <p
          className={`text-xs mt-1.5 ${
            isUser ? 'text-primary-foreground/60' : 'text-muted-foreground'
          }`}
        >
          {new Date(message.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-muted flex items-center justify-center">
        <Bot className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="bg-muted rounded-lg px-4 py-3 flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  );
}

const STARTER_PROMPTS = [
  'Explain quantum computing in simple terms',
  'Write a Python function to sort a list',
  'Summarize the benefits of MSP automation',
  'Draft an incident response runbook',
];

function EmptyState({ onSelectPrompt }: { onSelectPrompt: (prompt: string) => void }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-4">
      <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
        <Bot className="h-8 w-8 text-primary" />
      </div>
      <h2 className="text-xl font-semibold text-foreground mb-2">AI Playground</h2>
      <p className="text-muted-foreground max-w-md mb-6">
        Test AI models, experiment with prompts, and tune parameters.
        Configure the model and settings in the sidebar, then start a conversation.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg w-full">
        {STARTER_PROMPTS.map((prompt) => (
          <button
            key={prompt}
            className="text-left p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-accent/50 text-sm text-muted-foreground transition-colors"
            onClick={() => onSelectPrompt(prompt)}
          >
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
}
